// Admin > System Config. Reads /config and lets the admin tune integers
// in-place. Inline range validation mirrors the server-side bounds in
// apps/api/src/services/config/defaults.ts so users see errors before they
// submit.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, config } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import type { AppConfig } from '@frt/shared-types';

interface FieldDef {
  key: keyof AppConfig;
  label: string;
  description: string;
  min: number;
  max: number;
}

// NB: keep these aligned with apps/api/src/services/config/defaults.ts —
// the server is authoritative; FE bounds are a UX hint.
const FIELDS: FieldDef[] = [
  {
    key: 'month_end_cutoff_day',
    label: 'Month-end cutoff day',
    description: 'Day of month at which the period closes (1–31).',
    min: 1,
    max: 31,
  },
  {
    key: 'stale_threshold_days',
    label: 'Stale threshold (days)',
    description: 'Days since last upload before a Tiller account is "stale".',
    min: 0,
    max: 365,
  },
  {
    key: 'manual_entry_window_days_before',
    label: 'Manual entry window before cutoff (days)',
    description: 'Days before cutoff that the manual-entry window opens.',
    min: 0,
    max: 90,
  },
  {
    key: 'manual_entry_window_days_after',
    label: 'Manual entry window after cutoff (days)',
    description: 'Days after cutoff the window remains open.',
    min: 0,
    max: 90,
  },
  {
    key: 'manual_entry_pending_grace_days',
    label: 'Manual entry pending grace (days)',
    description: 'Grace days after cutoff before the pending banner fires.',
    min: 0,
    max: 90,
  },
  {
    key: 'large_transaction_threshold',
    label: 'Large transaction threshold',
    description: 'Transactions with abs(amount) ≥ this count as "large".',
    min: 0,
    max: 1_000_000,
  },
  {
    key: 'dashboard_stale_default_window_days',
    label: 'Dashboard stale-update default window (days)',
    description: 'Default "hide within N days" on the dashboard widget.',
    min: 0,
    max: 365,
  },
];

export function AdminConfigPage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const queryKey = ['admin', 'config'] as const;

  const { data: cfg, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => config.get(),
    enabled: !!me && me.role === 'admin',
  });

  const update = useMutation({
    mutationFn: (patch: Partial<AppConfig>) => config.update(patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const [draft, setDraft] = useState<Partial<Record<keyof AppConfig, string>>>({});
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    if (cfg) {
      setDraft({
        month_end_cutoff_day: String(cfg.month_end_cutoff_day),
        stale_threshold_days: String(cfg.stale_threshold_days),
        manual_entry_window_days_before: String(cfg.manual_entry_window_days_before),
        manual_entry_window_days_after: String(cfg.manual_entry_window_days_after),
        manual_entry_pending_grace_days: String(cfg.manual_entry_pending_grace_days),
        large_transaction_threshold: String(cfg.large_transaction_threshold),
        dashboard_stale_default_window_days: String(cfg.dashboard_stale_default_window_days),
      });
    }
  }, [cfg]);

  if (!me || me.role !== 'admin') {
    return (
      <div className="text-sm text-slate-600">System config requires the admin role.</div>
    );
  }

  // Compute per-field validation client-side.
  const fieldErrors: Partial<Record<keyof AppConfig, string>> = {};
  for (const f of FIELDS) {
    const raw = draft[f.key];
    if (raw === undefined || raw === '') continue;
    const n = Number(raw);
    if (!Number.isInteger(n)) {
      fieldErrors[f.key] = 'must be an integer';
    } else if (n < f.min || n > f.max) {
      fieldErrors[f.key] = `must be in [${f.min}, ${f.max}]`;
    }
  }
  const hasErrors = Object.keys(fieldErrors).length > 0;

  // Build the diff vs. the server state so we only send changed fields.
  const diff: Partial<AppConfig> = {};
  if (cfg) {
    for (const f of FIELDS) {
      const raw = draft[f.key];
      if (raw === undefined || raw === '') continue;
      const n = Number(raw);
      if (!Number.isInteger(n)) continue;
      if (n !== cfg[f.key]) (diff as Record<string, number>)[f.key] = n;
    }
  }
  const dirty = Object.keys(diff).length > 0;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900">System Config</h1>
      <Card>
        <CardHeader>
          <CardTitle>Tunables</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
          {isLoading || !cfg ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (hasErrors || !dirty) return;
                setSaveMessage(null);
                update.mutate(diff, {
                  onSuccess: () => setSaveMessage('Saved.'),
                  onError: (err) =>
                    setSaveMessage(
                      err instanceof ApiError ? err.message : String(err),
                    ),
                });
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {FIELDS.map((f) => (
                  <label key={f.key} className="block text-sm">
                    <span className="font-medium text-slate-800">{f.label}</span>
                    <Input
                      type="number"
                      min={f.min}
                      max={f.max}
                      value={draft[f.key] ?? ''}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, [f.key]: e.target.value }))
                      }
                    />
                    <span className="block text-xs text-slate-500">{f.description}</span>
                    {fieldErrors[f.key] && (
                      <span className="block text-xs text-red-600">
                        {fieldErrors[f.key]}
                      </span>
                    )}
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Button type="submit" disabled={hasErrors || !dirty || update.isPending}>
                  {update.isPending ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={!dirty}
                  onClick={() => {
                    if (cfg) {
                      setDraft({
                        month_end_cutoff_day: String(cfg.month_end_cutoff_day),
                        stale_threshold_days: String(cfg.stale_threshold_days),
                        manual_entry_window_days_before: String(
                          cfg.manual_entry_window_days_before,
                        ),
                        manual_entry_window_days_after: String(
                          cfg.manual_entry_window_days_after,
                        ),
                        manual_entry_pending_grace_days: String(
                          cfg.manual_entry_pending_grace_days,
                        ),
                        large_transaction_threshold: String(cfg.large_transaction_threshold),
                        dashboard_stale_default_window_days: String(
                          cfg.dashboard_stale_default_window_days,
                        ),
                      });
                      setSaveMessage(null);
                    }
                  }}
                >
                  Discard changes
                </Button>
                {saveMessage && (
                  <span
                    className={
                      update.isError ? 'text-sm text-red-600' : 'text-sm text-emerald-700'
                    }
                  >
                    {saveMessage}
                  </span>
                )}
              </div>
            </form>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
